import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core'
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs'
import { distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators'

import { MatSnackBar } from '@angular/material/snack-bar'

import { LocalMonth, NumberMonth } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'

import {
  OmaveroYhteisomyyntiDraft,
  OmaveroYhteisomyyntiDraftPuuttuvaVat,
  OmaveroYhteisomyyntilaskuLahetetty,
  OmaveroYhteisomyyntilaskuLahetettyRivi,
} from '@lemon/lemonaid-lemonator-functions-and-lemonator-angular/model/omavero'
import { AsiakasService, AsiakkaanAvainTiedot } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { AsiakasUriService } from 'app/_jaettu-lemonator/service/asiakas-uri.service'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'

import { KirjanpitajanRooli } from '@lemon/core-firebase/lemonator/model/kirjanpitaja'
import { DebugService } from 'app/_angular/service/debug.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { KirjanpitoKuukausiruutu } from 'app/_jaettu-lemonator/model/asiakas'
import { YhteisomyyntiIlmoitusLahetaIlmoitinTyo } from '../_jaettu-lemonator/model/kirjanpito'

export interface ListausYhteisomyyntiIlmoitus extends OmaveroYhteisomyyntilaskuLahetetty {
  url: string
  uri: string
  tallennustiedot: string
}

@Component({
  selector: '[app-kirjanpito-yhteisomyynti-ilmoitus]',
  templateUrl: './yhteisomyynti-ilmoitus.component.html',
  styleUrls: ['./yhteisomyynti-ilmoitus.component.css', './alv-ilmoitus.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjanpitoYhteisomyyntiIlmoitusComponent implements OnInit, OnDestroy {
  @Input() valittuKuukausiObservable: Observable<LocalMonth>
  @Output() nykyisellaKuukaudellaOnMuutoksia = new EventEmitter<'yes' | 'no' | 'not-saved-yet'>()

  private _ngUnsubscribe = new Subject<void>()

  draftLoadingSubject = new BehaviorSubject<boolean>(true)
  historyLoadingSubject = new BehaviorSubject<boolean>(true)
  private _tallentamattomiaMuutoksiaOverrideSubject = new BehaviorSubject<boolean | null>(null)

  draftObservable: Observable<OmaveroYhteisomyyntiDraft | null>
  viimeisinLahetettyObservable: Observable<OmaveroYhteisomyyntilaskuLahetetty | null>
  aikaisemmatIlmoituksetObservable: Observable<ListausYhteisomyyntiIlmoitus[]>
  tallentamattomiaMuutoksiaObservable: Observable<boolean>
  voidaanLahettaaObservable: Observable<boolean>
  voidaanLahettaaVaikkaTiedotEivatMuuttuneetObservable: Observable<boolean>
  kuukausiNumberObservable: Observable<NumberMonth>
  asiakasObservable: Observable<AsiakkaanAvainTiedot>
  errorMessageObservable: Observable<string | null>
  lahetysvirheObservable: Observable<string | null>
  puuttuvatVatKirjauksetObservable: Observable<OmaveroYhteisomyyntiDraftPuuttuvaVat[]>
  draftFirestoreLinkObservable: Observable<{ uri: string; url: string } | null>

  constructor(
    private _firebase: FirebaseLemonator,
    private _asiakasService: AsiakasService,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _asiakasUriService: AsiakasUriService,
    private _dateService: DateService,
    private _timestampService: TimestampService,
    private _debugService: DebugService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _ladataanService: LadataanService,
    private _snackbar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.kuukausiNumberObservable = this.valittuKuukausiObservable.pipe(map(kuukausi => this._dateService.localMonthToNumber(kuukausi)))

    this.asiakasObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(takeUntil(this._ngUnsubscribe))

    const hyvaksymattomatKirjauksetRaakadataObservable: Observable<KirjanpitoKuukausiruutu[]> = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (asiakas) {
          const collectionUri = this._asiakasUriService.annaListausRuutuCollectionUri(asiakas.avain)
          return this._firebase.firestoreCollection<KirjanpitoKuukausiruutu>(collectionUri).where('u', '>', 0).listen()
        }
        return of<KirjanpitoKuukausiruutu[]>([])
      }),
    )

    const hyvaksymattomatKirjauksetKuukaudellaObservable: Observable<KirjanpitoKuukausiruutu[]> = combineLatest([this.kuukausiNumberObservable, hyvaksymattomatKirjauksetRaakadataObservable]).pipe(
      map(([kuukausiNumber, ruudut]) => {
        if (kuukausiNumber && ruudut) {
          return ruudut.filter(a => a.k === kuukausiNumber)
        }
        return []
      }),
    )

    const asiakasJaKuukausiObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this.kuukausiNumberObservable])

    this.draftObservable = asiakasJaKuukausiObservable.pipe(
      tap(() => this.draftLoadingSubject.next(true)),
      switchMap(([asiakas, kuukausiNumber]) => {
        if (!asiakas) {
          return of(null)
        }
        const draftUri = this._kirjanpitoUriService.annaYhteisomyyntiIlmoituksenDraftUri(asiakas.avain, kuukausiNumber)
        return this._firebase.firestoreDoc<OmaveroYhteisomyyntiDraft>(draftUri).listen()
      }),
      tap(() => this.draftLoadingSubject.next(false)),
      takeUntil(this._ngUnsubscribe),
    )

    this.puuttuvatVatKirjauksetObservable = this.draftObservable.pipe(map(draft => draft?.puuttuvatVatKirjaukset || []))

    this.lahetysvirheObservable = this.draftObservable.pipe(map(draft => draft?.lahetysvirhe ?? null))

    this.draftFirestoreLinkObservable = combineLatest([
      this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable,
      this._asiakasService.nykyinenAsiakasAvainObservable,
      this.kuukausiNumberObservable,
    ]).pipe(
      map(([kirjanpitaja, asiakas, kuukausiNumber]) => {
        if (kirjanpitaja?.rooli === KirjanpitajanRooli.SUPER && asiakas && kuukausiNumber) {
          const uri = this._kirjanpitoUriService.annaYhteisomyyntiIlmoituksenDraftUri(asiakas.avain, kuukausiNumber)
          return { uri, url: this._debugService.createFirestoreLink(uri) }
        }
        return null
      }),
    )

    // Lataa kaikki lähetetyt ilmoitukset kyseiselle kuukaudelle
    const aikaisemmatIlmoituksetRaakadataObservable = asiakasJaKuukausiObservable.pipe(
      tap(() => this.historyLoadingSubject.next(true)),
      switchMap(([asiakas, kuukausiNumber]) => {
        if (!asiakas) {
          return of<OmaveroYhteisomyyntilaskuLahetetty[]>([])
        }
        const lahetettyCollectionUri = this._kirjanpitoUriService.annaOmaveroYhteisomyyntiIlmoitusLahetettyCollectionUri(asiakas.avain)
        return this._firebase.firestoreCollection<OmaveroYhteisomyyntilaskuLahetetty>(lahetettyCollectionUri).where('jakso', '==', kuukausiNumber).listen()
      }),
      map(ilmoitukset => {
        return ilmoitukset.sort((a, b) => b.lahetetty.toMillis() - a.lahetetty.toMillis())
      }),
      takeUntil(this._ngUnsubscribe),
    )

    this.aikaisemmatIlmoituksetObservable = combineLatest([
      this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable,
      this._asiakasService.nykyinenAsiakasAvainObservable,
      aikaisemmatIlmoituksetRaakadataObservable,
      this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable,
    ]).pipe(
      map(([kirjanpitaja, asiakas, ilmoitukset, kirjanpitajienNimitiedotMap]) => {
        if (!kirjanpitaja || !asiakas || !ilmoitukset) {
          return []
        }
        const populateDebugUrls = kirjanpitaja.rooli === KirjanpitajanRooli.SUPER
        return ilmoitukset.map(ilmoitus => {
          const asListaus: ListausYhteisomyyntiIlmoitus = ilmoitus as ListausYhteisomyyntiIlmoitus

          const nimitiedot = kirjanpitajienNimitiedotMap.get(ilmoitus.tallentajaAvain)
          const date = this._dateService.timestampToDate(ilmoitus.lahetetty)
          asListaus.tallennustiedot = (nimitiedot ? nimitiedot.etunimi + ' ' + nimitiedot.sukunimi : 'Lemonator System') + ' ' + this._dateService.muotoilePaivaJaAikaDate(date, 'fi')
          if (populateDebugUrls) {
            asListaus.uri = this._kirjanpitoUriService.annaOmaveroYhteisomyyntiIlmoitusLahetettyUri(asiakas.avain, ilmoitus.lahetysAvain)
            asListaus.url = this._debugService.createFirestoreLink(asListaus.uri)
          } else {
            asListaus.uri = null
            asListaus.url = null
          }
          return asListaus
        })
      }),
      tap(() => this.historyLoadingSubject.next(false)),
      takeUntil(this._ngUnsubscribe),
    )

    this.viimeisinLahetettyObservable = this.aikaisemmatIlmoituksetObservable.pipe(map(ilmoitukset => (ilmoitukset && ilmoitukset.length > 0 ? ilmoitukset[0] : null)))

    const laskettuMuutoksiaObservable = combineLatest([this.draftObservable, this.viimeisinLahetettyObservable]).pipe(
      map(([draft, lahetetty]) => {
        if (!draft) {
          return false
        }
        if (draft.tarvitseeLahetyksen) {
          return true
        }
        // Käyttäjän pitää voida manuaalisesti lähettää tyhjä korjaus
        if (draft.rivit.length === 0 && (lahetetty?.rivit?.length ?? 0) > 0) {
          return true
        }
        return false
      }),
      takeUntil(this._ngUnsubscribe),
    )

    // Yhdistä laskettu arvo ja manuaalinen yliajo (käytetään lähetyksen jälkeen)
    this.tallentamattomiaMuutoksiaObservable = combineLatest([laskettuMuutoksiaObservable, this._tallentamattomiaMuutoksiaOverrideSubject]).pipe(
      map(([laskettu, yliajo]) => {
        // Jos yliajo on asetettu (ei null), käytä sitä; muuten käytä laskettua arvoa
        return yliajo !== null ? yliajo : laskettu
      }),
      takeUntil(this._ngUnsubscribe),
    )

    this.errorMessageObservable = combineLatest([this.kuukausiNumberObservable, hyvaksymattomatKirjauksetKuukaudellaObservable, this.puuttuvatVatKirjauksetObservable]).pipe(
      map(([kuukausiNumber, hyvaksymattomatRuudut, puuttuvatVat]) => {
        if (puuttuvatVat?.length) {
          return 'Yhteisömyynti-ilmoitusta ei voi lähettää, koska seuraavissa kirjauksissa on yhteisömyynti-ALV mutta ei ALV-numeroa tai se on virheellinen.'
        }
        if (hyvaksymattomatRuudut?.length && kuukausiNumber) {
          const kuukausi = this._dateService.numberToLocalMonth(kuukausiNumber)
          const tunnistetiedot = kuukausi.month + '/' + kuukausi.year
          return 'Et voi lähettää yhteisömyynti-ilmoitusta ennen kuin olet hyväksynyt kaikki kuukauden ' + tunnistetiedot + ' kirjaukset.'
        }
        return null
      }),
    )

    // Voidaanko lähettää (pitää olla muutoksia, ei hyväksymättömiä kirjauksia, ei puuttuvia VAT-tunnuksia)
    this.voidaanLahettaaObservable = combineLatest([this.tallentamattomiaMuutoksiaObservable, hyvaksymattomatKirjauksetKuukaudellaObservable, this.puuttuvatVatKirjauksetObservable]).pipe(
      map(([tallentamattomiaMuutoksia, hyvaksymattomatKirjauksetKuukaudella, puuttuvatVat]) => {
        return tallentamattomiaMuutoksia && hyvaksymattomatKirjauksetKuukaudella.length < 1 && (!puuttuvatVat || puuttuvatVat.length < 1)
      }),
      takeUntil(this._ngUnsubscribe),
    )

    this.voidaanLahettaaObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(voidaan => {
      this._voidaanLahettaa = voidaan
    })

    // Voidaanko lähettää uudelleen vaikka tiedot eivät muuttuneet (vain superit)
    this.voidaanLahettaaVaikkaTiedotEivatMuuttuneetObservable = combineLatest([
      this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable,
      this.tallentamattomiaMuutoksiaObservable,
      hyvaksymattomatKirjauksetKuukaudellaObservable,
      this.puuttuvatVatKirjauksetObservable,
    ]).pipe(
      map(([kirjanpitaja, tallentamattomiaMuutoksia, hyvaksymattomatKirjauksetKuukaudella, puuttuvatVat]) => {
        return kirjanpitaja?.rooli === KirjanpitajanRooli.SUPER && hyvaksymattomatKirjauksetKuukaudella.length < 1 && !tallentamattomiaMuutoksia && (!puuttuvatVat || puuttuvatVat.length < 1)
      }),
    )

    // Nollaa yliajo kun kuukausi vaihtuu
    this.kuukausiNumberObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
      this._tallentamattomiaMuutoksiaOverrideSubject.next(null)
    })

    // Nollaa yliajo kun draftin sisältö muuttuu (esim. kirjaus päivitetty), jotta laskettu arvo pääsee voimaan
    this.draftObservable
      .pipe(
        map(draft => (draft ? JSON.stringify(draft.rivit) + JSON.stringify(draft.puuttuvatVatKirjaukset ?? []) : null)),
        distinctUntilChanged(),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(() => {
        this._tallentamattomiaMuutoksiaOverrideSubject.next(null)
      })

    combineLatest([this.draftObservable, this.tallentamattomiaMuutoksiaObservable, this.puuttuvatVatKirjauksetObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([draft, onMuutoksia, puuttuvatVat]) => {
        if (!draft) {
          this.nykyisellaKuukaudellaOnMuutoksia.emit('no')
        } else if (onMuutoksia || puuttuvatVat?.length > 0) {
          this.nykyisellaKuukaudellaOnMuutoksia.emit('yes')
        } else {
          this.nykyisellaKuukaudellaOnMuutoksia.emit('no')
        }
      })
  }

  ngOnDestroy(): void {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  computeTotalsForAlvRivi(rivit: OmaveroYhteisomyyntilaskuLahetettyRivi[]) {
    const rivitMap = new Map<string, OmaveroYhteisomyyntilaskuLahetettyRivi>()

    for (const rivi of rivit) {
      const nykyinenRivi = rivitMap.get(rivi.alvTunnus) || {
        maaKoodi: rivi.maaKoodi,
        alvTunnus: rivi.alvTunnus,
        tavaramyynnit: 0,
        palvelumyynnit: 0,
        kolmikantamyynnit: 0,
      }
      nykyinenRivi.tavaramyynnit += rivi.tavaramyynnit
      nykyinenRivi.palvelumyynnit += rivi.palvelumyynnit
      nykyinenRivi.kolmikantamyynnit += rivi.kolmikantamyynnit
      rivitMap.set(rivi.alvTunnus, nykyinenRivi)
    }
    return Array.from(rivitMap.values())
  }

  formatNumberMonthForDisplay(numberMonth: NumberMonth): string {
    const month = numberMonth % 100
    const year = Math.floor(numberMonth / 100)
    return `${month} / ${year}`
  }

  private _saveInflight = false
  private _voidaanLahettaa = false
  async laheta(yliajo: boolean = false): Promise<void> {
    if (this._saveInflight || (!this._voidaanLahettaa && !yliajo)) {
      return
    }
    if (yliajo) {
      const voidaanLahettaaVaikkaTiedotEivatMuuttuneet = await firstValueFrom(this.voidaanLahettaaVaikkaTiedotEivatMuuttuneetObservable)
      if (!voidaanLahettaaVaikkaTiedotEivatMuuttuneet) {
        return
      }
    }

    this._saveInflight = true

    this._ladataanService.aloitaLataaminen()
    console.log('Lähetetään yhteisömyynti-ilmoitus')
    try {
      const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
      await this._firebase.functionsCall<YhteisomyyntiIlmoitusLahetaIlmoitinTyo, void>('kirjanpitoYhteisomyyntiIlmoitusIlmoitinLahetaCreated', {
        luotu: this._timestampService.now(),
        asiakasAvain: asiakas.avain,
        jakso: await firstValueFrom(this.valittuKuukausiObservable),
      })

      // Aseta yliajo että nappi disabloituu heti, ennen kuin backend ehtii prosessoida
      // ja Firestore-listenerit päivittyä
      this._tallentamattomiaMuutoksiaOverrideSubject.next(false)

      this._snackbar.open('Yhteisömyynti-ilmoitus lähetetty onnistuneesti.', 'OK', { duration: 2500 })
    } catch (err) {
      console.error('Yhteisömyynti-ilmoituksen lähetys epäonnistui', err)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._saveInflight = false
    }
  }
}
